import type { FC } from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import * as creditAppSearchSelectors from '@makemydeal/dr-dash-store/selectors/creditAppSearchSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as creditAppSearchActionCreators from '@makemydeal/dr-dash-store/actions/creditAppSearchActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { Spinner } from '@interstate/components/Spinner';
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { useCreditAppSearchForm } from './hooks/useCreditAppSearchForm';
import { useSearchCreditApps } from './hooks/useSearchCreditApps';
import { useDealComparisonData } from './hooks/useDealComparisonData';
import { SearchView } from './views/SearchView';
import { SelectableResultsView } from './views/SelectableResultsView';
import { ReviewAttachView } from './views/ReviewAttachView';
import { getResultId } from './utils/resultCardMappers';
import { GrowingSlideoutContainer, LoadingContainer, StyledCreditAppSearchSlideOut } from './CreditAppSearch.styles';
import type { CreditAppSearchResult } from './types';
import type { FooterActionsType } from '@interstate/components/FooterActions';
import { dealLockHooks } from '../hooks';

const SEARCH_DESCRIPTION =
    'Search for customer information from leads, credit applications or saved deals to attach to the current deal.';

export const CreditAppSearchSlideOutV2: FC = () => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();
    const isSlideOutOpen = useSelector(creditAppSearchSelectors.getSlideOutOpen);
    const shopperInfo = useSelector(offerReduxSelectors.getShopperInfo);
    const lastName = shopperInfo.lastName || '';
    const orignalDealRefId = useSelector(offerReduxSelectors.getDealRefId);
    const originalDealRefIdFs = useSelector(offerReduxSelectors.getDealRefIdFS);
    const enableCorrectPhoneHandling = useSelector(featureToggleSelectors.isCorrectPhoneHandlingEnabled);
    const isLocked = dealLockHooks.useIsDealLocked();

    const { formData, errors, touched, handleFieldChange, handleFieldBlur, clearForm, isSearchButtonEnabled } =
        useCreditAppSearchForm({
            lastName
        });
    const { searchResults, isLoading, error, performSearch, clearResults, cancelSearch } = useSearchCreditApps();

    const [selectedCreditApp, setSelectedCreditApp] = useState<CreditAppSearchResult | null>(null);
    const [chosenResultId, setChosenResultId] = useState<string | null>(null);

    const hasInitializedLastName = useRef<boolean>(false);
    const hasPerformedInitialSearch = useRef<boolean>(false);

    const { currentDealData, newDealData } = useDealComparisonData(selectedCreditApp);

    const selectedResult = searchResults.find((result) => getResultId(result) === chosenResultId) ?? searchResults[0];

    useEffect(() => {
        if (isSlideOutOpen && lastName && !hasInitializedLastName.current) {
            handleFieldChange('lastName', lastName);
            hasInitializedLastName.current = true;
        }

        if (!isSlideOutOpen) {
            hasInitializedLastName.current = false;
        }
    }, [isSlideOutOpen, lastName, handleFieldChange]);

    useEffect(() => {
        if (isSlideOutOpen && lastName && !hasPerformedInitialSearch.current) {
            performSearch({ lastName, phone: '', email: '' });
            hasPerformedInitialSearch.current = true;
        }

        if (!isSlideOutOpen) {
            hasPerformedInitialSearch.current = false;
        }
    }, [isSlideOutOpen, lastName, performSearch]);

    const resetSearch = useCallback(() => {
        cancelSearch();
        clearForm();
        clearResults();
        setChosenResultId(null);
        setSelectedCreditApp(null);
    }, [cancelSearch, clearForm, clearResults]);

    const handleClose = useCallback(() => {
        resetSearch();
        dispatch(creditAppSearchActionCreators.closeCreditAppSearchSlideout());
    }, [resetSearch, dispatch]);

    const handleSearch = useCallback(async () => {
        setChosenResultId(null);
        await performSearch(formData);
    }, [formData, performSearch]);

    const handleClear = useCallback(() => {
        clearForm();
        clearResults();
        setChosenResultId(null);
    }, [clearForm, clearResults]);

    const handleBackToSearch = useCallback(() => {
        setSelectedCreditApp(null);
    }, []);

    const handleAcceptChanges = useCallback(
        (selected: CreditAppSearchResult) => {
            const currentDealRefId = orignalDealRefId || originalDealRefIdFs;
            dispatch(
                creditAppSearchActionCreators.mergeCreditAppDeal(selected.rawDeal, currentDealRefId, enableCorrectPhoneHandling)
            );
            dispatch(creditAppSearchActionCreators.showCreditAppSearchSaveReminder());
            resetSearch();
            dispatch(creditAppSearchActionCreators.closeCreditAppSearchSlideout());
        },
        [orignalDealRefId, originalDealRefIdFs, dispatch, enableCorrectPhoneHandling, resetSearch]
    );

    const cancelOnlyFooterProps: FooterActionsType = {
        layout: 'rightAligned',
        primary: {
            label: 'Cancel',
            action: handleClose,
            customIdentifiers: {
                primaryButton: {
                    dataTestId: 'credit-app-search-slideout-review-view-cancel-button'
                }
            }
        }
    };

    const buildAttachFooterProps = (selected: CreditAppSearchResult): FooterActionsType => ({
        layout: 'rightAligned',
        options: [
            {
                label: 'Cancel',
                buttonStyle: 'tertiary',
                action: handleClose,
                customIdentifiers: {
                    optionButton0: {
                        dataTestId: 'credit-app-search-slideout-review-view-cancel-button'
                    }
                }
            }
        ],
        primary: {
            label: 'Attach',
            action: () => setSelectedCreditApp(selected),
            disabled: isLocked,
            customIdentifiers: {
                primaryButton: {
                    dataTestId: 'credit-app-search-slideout-attach-button'
                }
            }
        }
    });

    const buildReviewFooterProps = (selected: CreditAppSearchResult): FooterActionsType => ({
        layout: 'rightAligned',
        options: [
            {
                label: 'Cancel',
                buttonStyle: 'tertiary',
                action: handleClose,
                customIdentifiers: {
                    optionButton0: {
                        dataTestId: 'credit-app-search-slideout-review-view-cancel-button'
                    }
                }
            },
            {
                label: 'Back',
                buttonStyle: 'secondary',
                action: handleBackToSearch,
                customIdentifiers: {
                    optionButton1: {
                        dataTestId: 'credit-app-search-slideout-review-view-back-button'
                    }
                }
            }
        ],
        primary: {
            label: 'Accept Changes',
            action: () => handleAcceptChanges(selected),
            disabled: isLocked,
            customIdentifiers: {
                primaryButton: {
                    dataTestId: 'credit-app-search-slideout-review-view-accept-changes-button'
                }
            }
        }
    });

    const searchFooterProps = selectedResult ? buildAttachFooterProps(selectedResult) : cancelOnlyFooterProps;

    return (
        <StyledCreditAppSearchSlideOut
            header={
                <Typography variant="h3">
                    {selectedCreditApp ? 'Search & Attach Deal Information' : 'Search & Attach Information'}
                </Typography>
            }
            show={isSlideOutOpen}
            panelWidth="auto"
            position="right"
            onHide={handleClose}
            data-testid="credit-app-search-slideout"
            footer={selectedCreditApp ? buildReviewFooterProps(selectedCreditApp) : searchFooterProps}
        >
            <ThemeProvider theme={theme}>
                <GrowingSlideoutContainer>
                    {!selectedCreditApp && (
                        <>
                            <SearchView
                                formData={formData}
                                errors={errors}
                                touched={touched}
                                isSearchButtonEnabled={isSearchButtonEnabled}
                                onFieldChange={handleFieldChange}
                                onFieldBlur={handleFieldBlur}
                                onClear={handleClear}
                                onSearch={handleSearch}
                                description={SEARCH_DESCRIPTION}
                            />

                            {isLoading && (
                                <LoadingContainer data-testid="search-loading">
                                    <Spinner />
                                    <Typography variant="body-md">Currently searching for Credit Apps...</Typography>
                                </LoadingContainer>
                            )}

                            {!isLoading && (
                                <SelectableResultsView
                                    results={searchResults}
                                    error={error}
                                    selectedResultId={selectedResult ? getResultId(selectedResult) : null}
                                    onSelect={setChosenResultId}
                                />
                            )}
                        </>
                    )}

                    {selectedCreditApp && newDealData && (
                        <ReviewAttachView currentDealData={currentDealData} newDealData={newDealData} />
                    )}
                </GrowingSlideoutContainer>
            </ThemeProvider>
        </StyledCreditAppSearchSlideOut>
    );
};
